import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { apiClient } from '../../../api'
import { useProperty } from '../../../Contexts/PropertyContext/PropertyContext'
import { baseUrl, stripePublicKey } from '../../../Environments/environment'
import { toast } from 'react-toastify'

const stripePromise = loadStripe(stripePublicKey)

/**
 * CheckoutContent renders inside Elements when clientSecret is available
 */
function CheckoutContent({ amountDisplay, orderId, onPaymentSuccess, onClose, onError }) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    if (!stripe || !elements) {
      setLoading(false)
      return
    }

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      })

      // CASE 1: UI/frame/validation errors → don't navigate
      if (result.error) {
        console.error('Stripe UI error:', result.error)
        toast.error(result.error.message)
        setLoading(false)
        // Close all modals on error
        if (onError) {
          onError()
        }
        onClose()
        return
      }

      // CASE 2: Payment succeeds
      if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
        // Update order with payment information
        // try {
        //   const propertyId = localStorage.getItem('selectedPropertyId')
        //   const token = localStorage.getItem('accessToken')

        //   const response = await apiClient.put(
        //     `${baseUrl}/qrKitOrder/${propertyId}/${orderId}`,
        //     {
        //       orderPayment: {
        //         paymentId: result.paymentIntent.id,
        //         paymentStatus: 'succeeded',
        //         paymentAmount: result.paymentIntent.amount / 100, // Convert from cents
        //       },
        //     },
        //     {
        //       headers: {
        //         Authorization: `Bearer ${token}`,
        //       },
        //     }
        //   )

        //   if (response.status === 200 || response.status === 201) {
        //     toast.success('Payment successful!')
        //     toast.success('Order placed successfully!')
        //     if (onPaymentSuccess) {
        //       onPaymentSuccess(result.paymentIntent)
        //     }
        //     onClose()
        //   }
        // } catch (error) {
        //   console.error('Error updating order:', error)
        //   toast.error(error.response?.data?.message || 'Payment successful but failed to update order.')
        // }
        console.log('Payment successful!')
        if (onPaymentSuccess) {
            toast.success('Order Placed Successfully!')
            onPaymentSuccess(result.paymentIntent)
        }
        onClose()
      }
    } catch (err) {
      // CASE 3: Critical system crash (Rare)
      console.error('Unexpected error:', err)
      toast.error('Something went wrong. Please try again.')
      // Close all modals on error
      if (onError) {
        onError()
      }
      onClose()
    } finally {
      setLoading(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-md mx-auto p-6 border rounded-md bg-white"
    >
      <div className="mb-4 text-sm text-gray-600">Enter payment details</div>
      <div className="md:min-w-[400px] min-w-0 ">
        <PaymentElement />
      </div>
      <button
        type="submit"
        disabled={!stripe || loading}
        className="mt-4 w-full px-4 py-2 bg-indigo-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Processing...' : `Pay ${amountDisplay} & Save Card`}
      </button>
    </form>
  )
}

const CheckoutModal = ({ isOpen, onClose, totalAmount, orderData, onPaymentSuccess, onError }) => {
  const { selectedPropertyId } = useProperty()
  const [clientSecret, setClientSecret] = useState('')
  const [loading, setLoading] = useState(false)
  const [orderId, setOrderId] = useState(null)

  const createOrder = async () => {
    try {
      const propertyId = localStorage.getItem('selectedPropertyId')
      const token = localStorage.getItem('accessToken')

      const requestBody = {
        ...orderData,
        orderPayment: {
          paymentId: 'ABCXYZ',
          paymentStatus: 'pending',
          paymentAmount: orderData.orderAmount,
        },
      }

      const response = await apiClient.post(
        `${baseUrl}/qrKitOrder/${propertyId}`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.status === 200 || response.status === 201) {
        console.log('Order created successfully', response.data.result)
        const createdOrderId = response.data.result.qrKitOrder._id
        setOrderId(createdOrderId)
        return createdOrderId
      }
      return null
    } catch (error) {
      console.error('Error creating order:', error)
      toast.error(error.response?.data?.message || 'Failed to create order. Please try again.')
      return null
    }
  }

  const createPaymentIntent = async (orderId) => {
    try {
      setLoading(true)
      const propertyId = selectedPropertyId

      // Fetch wallet to get customerId
      const walletResponse = await apiClient.get(
        `${baseUrl}/stripe/wallet/${propertyId}`
      )
      const wallet = walletResponse.data.result
      const customerId = wallet?.stripeCustomerId

      if (!customerId) {
        toast.error('No Stripe customer found. Create a customer first.')
        onClose()
        return
      }
      if(!orderId) {
        toast.error('Order ID is required')
        onClose()
        return
      }

      const amountCents = Math.round(totalAmount * 100)

      // Create payment intent
      const response = await apiClient.post(
        `${baseUrl}/purchaseStripe/create-payment-intent`,
        {
          customerId,
          propertyId,
          userId: wallet._id,
          amount: amountCents,
          orderId: orderId,
        }
      )

      const { clientSecret } = response.data.result
      setClientSecret(clientSecret)
    } catch (err) {
      console.error('Failed creating payment intent:', err)
      toast.error(err.response?.data?.message || 'Failed to initialize payment. Please try again.')
      // Close all modals on error
      if (onError) {
        onError()
      }
      onClose()
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen && orderData) {
      // Create order first, then create payment intent
      createOrder().then((createdOrderId) => {
        if (createdOrderId) {
          createPaymentIntent(createdOrderId)
        } else {
          // Order creation failed, close all modals
          if (onError) {
            onError()
          }
          onClose()
        }
      }).catch((error) => {
        console.error('Error creating order:', error)
        // Order creation failed, close all modals
        if (onError) {
          onError()
        }
        onClose()
      })
    } else {
      // Reset when modal closes
      setClientSecret('')
      setOrderId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (!isOpen) return null

  const amountDisplay = `$${totalAmount.toFixed(2)}`

  if (!clientSecret || loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="text-center">
            <div className="text-gray-600">Preparing payment...</div>
          </div>
        </div>
      </div>
    )
  }

  const options = {
    clientSecret,
    appearance: { theme: 'stripe' },
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add New Card</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <Elements stripe={stripePromise} options={options}>
            <CheckoutContent
              amountDisplay={amountDisplay}
              orderId={orderId}
              onPaymentSuccess={onPaymentSuccess}
              onClose={onClose}
              onError={onError}
            />
          </Elements>
        </div>
      </div>
    </div>
  )
}

export default CheckoutModal
